<!-- 
Functions: 
	- udpateGrids
	- updateGridRows
-->

<script>
	/* 
	=======================================================
	START - DEPENDENCIES
	=======================================================
	*/
	import { createEventDispatcher, tick, setContext, onDestroy } from 'svelte'
	import { css } from '../../../../node_modules/@emotion/css/dist/emotion-css.umd.min.js'
	import { writable } from 'svelte/store'
	import { createStore } from './store'
	import { getId, createBaseEvents, exists, injectData } from '../../../core/index'
	import { paddingParse, cornerParse, addUnit } from '../../../core/parse'
	import { UFormData, WIN, nodeDim, decodeBase64 } from '../../../core/dom'
	import { setVariables, sizeCssVariables, transition } from '../../../core/css'
	import { chooseColor } from '../../../core/color'
	import { map } from '../../../core/collection'
	import { addConfig } from './component'
	import { getViewportRanges, getCurrentBreakpointViewport, adjustRowCellsWidth, getCell, processTriggers, pageSetup } from './Lineup'
	import Row from './Row.svelte'
	import Button from '../../button/src/Button.svelte'
	import Checkbox from '../../checkbox/src/Checkbox.svelte'
	import Media from '../../media/src/Media.svelte'
	import Shape from '../../shape/src/Shape.svelte'
	import Slider from '../../slider/src/Slider.svelte'
	import Switch from '../../switch/src/Switch.svelte'
	import Text from '../../text/src/Text.svelte'
	import Textfield from '../../textfield/src/Textfield.svelte'
	import { getBoxShadow } from '../../../core/elevation'
	import { rootClass } from './lineupStyle'

	/* 
	=======================================================
	END - DEPENDENCIES
	=======================================================
	*/









	/* 
	=======================================================
	START - CONSTANTS
	=======================================================
	*/

	const ID = getId() // e.g., '_cwd23d'
	const EDGE_THICKNESS = 20 // unit: pixels
	const COMPONENTS = [
		{ id:'button', class:Button },
		{ id:'checkbox', class:Checkbox },
		{ id:'media', class:Media },
		{ id:'shape', class:Shape },
		{ id:'slider', class:Slider },
		{ id:'switch', class:Switch },
		{ id:'text', class:Text },
		{ id:'textfield', class:Textfield }
	]

	/* 
	=======================================================
	END - CONSTANTS
	=======================================================
	*/









	/* 
	=======================================================
	START - COMPONENT PROPERTIES
	=======================================================
	*/

	// Content
	export let id = null 	// Required lineup component ID. 
	export let name = null
	export let grids = []
	export let componentDefs = [] // e.g., [{ id:'button', class:Button }]
	export let data = null
	// Style
	export let theme = null
	export let width = null
	export let height = null
	export let padding = 0
	export let margin = 0
	export let backgroundColor = null
	export let border = null // Border's thickness when idle
	export let borderColor = null
	export let borderRadius = null // Number or object (e.g., { left: 4, right:0 } or { top:4, bottom:2 } or { top: { left:1, right:2 }, bottom: { left:3, right:4 } } or { left: { top:1, bottom:2 }, right: { top:3, bottom:4 } })
	export let elevation = null
	export let elevationStrength = 0
	export let elevationColor = '#000'
	// Behavior
	export let page = null // { backgroundColor:'white' } if set, this component is an HTML page. This impacts the meta data.
	export let cursor = null // CSS cursor for hover effect
	export let deleteCounter = null // Hack to trigger deletion of selected icons
	export let edit = false
	/**
	 * onHover behavior
	 * 
	 * @param {Number}	onHover.elevation
	 * @param {Number}	onHover.translateX
	 * @param {Number}	onHover.translateY	
	 * @param {Number}	onHover.transitionDuration	Default 100.
	 * @param {String}	onHover.transitionCurve		Default 'ease-in-out'.
	 * 
	 */
	export let onHover = null
	/**
	 * Gets a dimension object that can return the cell or row dimensions. 
	 * 
	 * @param  {String} options.cellId
	 * @param  {String} options.rowId
	 * @return {[type]}         [description]
	 */
	export const findDimension = ({ cellId, rowId }) => {
		if (!cellDimensions || (!cellId && !rowId))
			return null 

		return cellId
			? cellDimensions.find(d => d && d.cellId == cellId)
			: cellDimensions.find(d => d && d.rowId == rowId)
	}

	/* 
	=======================================================
	END - COMPONENT PROPERTIES
	=======================================================
	*/









	/* 
	=======================================================
	START - COMPONENT EVENTS
	=======================================================
	*/

	const dispatch = createEventDispatcher()
	createBaseEvents(dispatch)
	const emitEvent = eventName => data => dispatch(eventName, data||{})
	
	/**
	 * on:cellclick
	 *
	 * @param  {Cell}		data.cell
	 * @param  {[String]}	data.selectedCellIds	Selected cell IDs 
	 * @param  {[Layer]}	data.layers
	 * 
	 * @return {Void}
	 */
	const emitCellSelect = emitEvent('cellselect')

	/**
	 * on:viewportchange
	 *
	 * @param  {Object}		data
	 * 
	 * @return {Void}
	 */
	const emitViewportChange = emitEvent('viewportchange')

	/**
	 * on:gridschange
	 *
	 * @param  {[Cells]}	data.cells
	 * 
	 * @return {Void}
	 */
	const emitGridsChange = emitEvent('gridschange')

	/**
	 * on:componentclick
	 *
	 * @param  {Cell} 	data.cell
	 * @param  {String}	data.layer
	 * @param  {Object}	data.data
	 * 
	 * @return {Void}
	 */
	const emitComponentClick = emitEvent('componentclick')

	/**
	 * on:componentvaluechange
	 *
	 * @param  {String} data.id					Cell's ID
	 * @param  {Object} data.value				Cell's component's value
	 * @param  {Object}	data.layer
	 * @param  {String} data.type				e.g., 'slider', 'textfield'	
	 * @param  {String} data.name				Cell's component's name
	 * 
	 * @return {Void}
	 */
	const emitComponentValueChange = emitEvent('componentvaluechange')

	/**
	 * on:layeradd
	 *
	 * @param  {String}	 data.cellId	
	 * @param  {Boolean} data.new	
	 * @param  {[Layer]} data.layers	
	 * 
	 * @return {Void}
	 */
	const emitLayerAdd = emitEvent('layeradd')

	/**
	 * on:cellmove
	 *
	 * @param  {String}	 data.id	
	 * @param  {Object}  data.to	
	 * 
	 * @return {Void}
	 */
	const emitCellMove = emitEvent('cellmove')

	/* 
	=======================================================
	END - COMPONENT EVENTS
	=======================================================
	*/









	/* 
	=======================================================
	START - REACTIVE STATE PROPS
	=======================================================
	*/

	let viewportWidth,
		store = createStore(),
		toggledCellLayers = {}, // { '_123':[0], '_456':[1,2] } WARNING: This is ignored in edit mode. 
		cellValues = [],
		cellDropPos, // track the relative position of the cell in the grid when a component or a cell is being dragged
		componentPreviewCssStyle = '',
		cellHoverEffectEnabled = true,
		getDim,
		noresize = false, // There a few cases in edit mode where resize should not happen (multi select, dragging).
		node,
		cellDimensions = [],
		selectedCellIds = [],
		cellSelectedStates = [],
		commandControlKeyDown = false,
		copiedCellIds,
		draggedCellId,
		lastXpos = null, // Last cursor X pos during a cell move. Used to determine the diretion of the move 
		lastYpos = null, // Last cursor Y pos during a cell move. Used to determine the diretion of the move 
		removeCssVariablesFromBody
	
	let dragging = store.dragging
	let resizing = store.resizing

	/* 
	=======================================================
	END - REACTIVE STATE PROPS
	=======================================================
	*/









	/* 
	=======================================================
	START - PRIVATE FUNCTIONS
	=======================================================
	*/

	setContext('cellvalues', {
		/**
		 * Gets the cell's reactive value. That value is set in this component in the 'mutateCellValues' function. 
		 * 
		 * @param  {String}   cellId
		 * @param  {String}   layer.id
		 * @param  {Object}   layer.component
		 * 
		 * @return {Reactive} value
		 */
		getReactiveCellLayerValue: (cellId, layer) => {
			if (!layer || !exists(layer.id) || !layer.component)
				return null 

			const component = layer.component
			const type = component.id == 'checkbox' && component.mode == 'radio' ? 'radio' : component.id 
			const inputName = component.name || `${type}${cellId}`

			const value = createCellValue({ 
				id: cellId, 
				layerId: layer.id,
				name: inputName,
				type,
				value: component.value === undefined ? null : component.value
			})

			const index = cellValues.findIndex(x => x.id == cellId && x.layerId == layer.id)

			// Add this reactive value to the store
			if (index < 0) {
				cellValues.push(value)
				return value.value
			} else { // Use the existing stored reactive value or update it if it has changed.
				const cellValue = cellValues[index]
				if (cellValue.name == value.name && cellValue.type == value.type)
					return cellValue.value
				else {
					cellValues[index] = value
					return value.value
				}
			}
		},

		getReactiveCellSelectedState: (cellId) => {
			let cellSelectedState = cellSelectedStates.find(x => x.id == cellId)

			if (!cellSelectedState) {
				cellSelectedState = createCellSelectedState(cellId)
				cellSelectedStates.push(cellSelectedState)
			} 

			return cellSelectedState.value
		}
	})

	/**
	 * Gets the rows based on the viewport width 'vw'.
	 * 
	 * @param  {[Grids]} grids		Grid for each viewport width
	 * @param  {Number}  vw			Viewport width
	 * 
	 * @return {[Row]}	 rows
	 */
	const renderBreakpointRows = (grids, vw) => {
		// Resert the selected cell when rows are re-rendered.
		// setSelectedCellIds([])

		if (!grids)
			return []
		const grid = grids.find(g => g.viewportWidth == vw)
		if (!grid)
			return []
		
		return grid.rows||[]
	}

	/**
	 * Returns an array of strings containing comma-separated layer's IDs for each cell in the row. 
	 * NOTE:
	 * 	- Why string rather than array? Because representing an array as a string won't cause re-render 
	 * 	when the array is regenerated but its content did not change.
	 * 	- The 'toggledCellLayers' might be mutated. This occurs when a cell's layer ID is tracked in
	 * 	'toggledCellLayers' and that layer ID is deleted. 
	 * 	
	 * @param  {String}  rows[].cells[].id					Cell's ID
	 * @param  {String}  rows[].cells[].layers[].id			Cell's layer ID
	 * @param  {Boolean} rows[].cells[].layers[].toggled	True means the layer is visible
	 * @param  {Boolean} rows[].cells[].layers[].locked		True means the layer's 'toggled' value cannot be mutated by a trigger.
	 * @param  {Object}  toggledCellLayers					{ '_123':[1], '_321':[0], '_456':[0,1] }
	 * 
	 * @return {String}  rowLayers							['_123:0;1,_789:,_912:1', '_321:1,_98:2;4,_543:3,_987:4,_341:5'] 
	 */
	const getToggledRowLayersAndSetToggledCellLayers = (rows, toggledCellLayers) => {
		const rl = rows.length
		const toggledRowLayers = new Array(rl)

		for (let i=0;i<rl;i++) {
			const cells = rows[i].cells
			const cl = cells.length
			const newToggledCellLayers = new Array(cl)
			for (let j=0;j<cl;j++) {
				const cell = cells[j]
				if (!cell.layers) {
					newToggledCellLayers[j] = `${cell.id}:`
					continue
				}

				const availableLayerIds = cell.layers.map(x => x.id)
				const currentToggledLayerIds = toggledCellLayers[cell.id]
				if (currentToggledLayerIds) {
					const [validIds, invalidIds] = currentToggledLayerIds.reduce((acc,i) => {
						if (availableLayerIds.some(x => x == i))
							acc[0].push(i)
						else
							acc[1].push(i)
						return acc
					},[[],[]])

					const vl = validIds.length
					const il = invalidIds.length
					if (il)
						// Delete the invalidIds from 'toggledCellLayers' as it has been deleted by another process.
						toggledCellLayers[cell.id] = vl ? validIds : null

					if (vl) {
						newToggledCellLayers[j] = `${cell.id}:${validIds.join(';')}`
						continue
					}
				}

				const toggledLayerIds = cell.layers.filter(x => x.toggled).map(x => x.id)
				toggledCellLayers[cell.id] = toggledLayerIds
				newToggledCellLayers[j] = `${cell.id}:${toggledLayerIds.join(';')}`
			}
			toggledRowLayers[i] = newToggledCellLayers.join(',')
		}

		return toggledRowLayers
	}

	/**
	 * Creates the mutations that duplicate an entire row of cells in a new row under. 
	 * 
	 * @param  {Row} 	row			Cells to be copied under.
	 * @param  {Number} rowIndex	Current row index
	 * 
	 * @return {Row} 	mut[].newRow.row
	 * @return {String} mut[].newRow.belowRowIndex	
	 */
	const getRowPasteMutations = (row, rowIndex) => {
		return [{
			newRow: {
				belowRowIndex: rowIndex,
				row
			}
		}]
	}

	/**
	 * Creates the mutations that duplicate certain cells in row in the same row.
	 * 
	 * @param  {[Cell]} rowCells		Row cells to be copied.
	 * @param  {Number} rowIndex		Current row index
	 * 
	 * @return {String} mut[].id					Cell's ID	
	 * @return {String} mut[].newCell.width
	 * @return {String} mut[].newCell.widthUnit	
	 * @return {String} mut[].newCell.rowIndex
	 * @return {String} mut[].newCell.neighbourId
	 * @return {String} mut[].newCell.neighbourSide	
	 * @return {Object} mut[].newCell.layers		[{ id:0, toggled:true, component:{ id:'textfield', type:'native' } }]
	 */
	const getCellsPasteMutations = (rowCells, rowIndex) => {
		return rowCells.map(c => ({
			id: getId(),
			newCell: {
				width: c.widthUnit == 'px' ? c.width : null,
				widthUnit: c.widthUnit == 'px' ? c.widthUnit : null,
				rowIndex,
				neighbourId: c.id,
				neighbourSide: 'right',
				backgroundColor: c.backgroundColor,
				aspectRatio: c.aspectRatio ? { ...c.aspectRatio } : null,
				layers: c.layers.map(l => cloneLayer(l))
			}
		}))
	}

	const paste = (cellIds) => {
		if (!cellIds || !cellIds[0])
			return

		const cellMutations = []
		const rowMutations = []
		for (let rowIndex=0;rowIndex<rows.length;rowIndex++) {
			const row = rows[rowIndex]
			const rowCells = row.cells
			const selectedCells = rowCells.filter(c => cellIds.includes(c.id))
			const l = selectedCells.length
			if (!l)
				continue

			if (l == rowCells.length)
				rowMutations.push(...getRowPasteMutations(row, rowIndex))
			else
				cellMutations.push(...getCellsPasteMutations(selectedCells, rowIndex))
		}

		udpateGrids(cellMutations, rowMutations, { caller:'paste' })
	}

	const createCellSelectedState  = cellId => {
		let v = { selected:false, multi:false }
		const reactiveValue = writable(v) 
		return {
			id: cellId,
			getValue: () => v,
			setValue: val => {
				v = val 
				reactiveValue.set(val)
			},
			value: reactiveValue
		}
	}

	/**
	 * 
	 *
	 * @param  {String}		cell.id				Cell's ID (legacy. Use 'cellId' instead)
	 * @param  {String}		cell.cellId			Cell's ID
	 * @param  {String}		cell.rowId			Row's ID
	 * @param  {Function}	cell.getRowDim
	 * @param  {Function}	cell.getDim	
	 * 
	 * @return {Void}
	 */
	const registerCell = cell => {
		if (!cell)
			return 

		cell.ts = Date.now()
		cellDimensions.push(cell)

	}

	const deregisterCell = cellId => {
		if (!cellId)
			return 

		const results = cellDimensions.reduce((acc,c,i) => {
			if (c.id == cellId)
				acc.push({ ts:c.ts, i })
			return acc
		}, []).sort((a,b) => b.ts-a.ts)

		const l = results.length
		if (!l)
			return

		if (l == 1)
			cellDimensions.splice(results[0].i,1)
		else 
			results.slice(1).forEach(r => cellDimensions.splice(r.i,1))

	}

	/**
	 * Create a store of reactive cell values. Those values help updating a cell's value in this component
	 * and create an automatic update in the cell without forcing a re-rendering of the row and the other 
	 * cells.
	 * 
	 * @param  {String}   id			Cell's ID
	 * @param  {Object}   layerId		Cell's layer ID
	 * @param  {String}   name			Cell's active component field name
	 * @param  {String}   type			e.g., 'textfield', 'checkbox', 'radio'
	 * @param  {Object}   value	
	 * 
	 * @return {String}   id			Cell's ID
	 * @return {String}   name			Cell's active component field name
	 * @return {String}   type			e.g., 'textfield', 'checkbox', 'radio'
	 * @return {React}    value			Reactive version of the input's value.
	 * @return {Function} getValue		Gets the explicit value
	 * @return {Function} setValue		Update the reactive as well as the explicit value.
	 */
	const createCellValue = ({ id, layerId, name, type, value }) => {
		let v = !value || typeof(value) != 'object' ? value : { ...value }
		const reactiveValue = writable(v)
		return {
			id, 
			layerId,
			name, 
			type,
			value: reactiveValue,
			getValue: () => v,
			setValue: val => {
				v = val
				reactiveValue.set(val)
			}
		}
	}

	/**
	 * Mutates cell values of type radio. 
	 * 
	 * @param  {[Object]} cellValues
	 * @param  {String}	  cellId
	 * @param  {Object}	  layerId
	 * @param  {Object}   value	
	 * @param  {String}   fieldName	
	 * 
	 * @return {Void}			
	 */
	const mutateRadioCellValues = (cellValues, cellId, layerId, value, fieldName) => {
		const val = value ? value : false

		for (let c of cellValues) {
			if (c.type == 'radio' && c.name == fieldName) {
				if (c.id == cellId && c.layerId == layerId) {
					if (c.getValue() != val)
						c.setValue(val)
				}
				else if (value)
					c.setValue(false)
			}
		}
	}

	/**
	 * Mutates standard cell values (i.e., non radio)
	 * 
	 * @param  {String}	  cellId
	 * @param  {Object}	  layerId
	 * @param  {Object}   value	
	 * 
	 * @return {Void}			
	 */
	const mutateStandardCellValues = (cellValues, cellId, layerId, value) => {
		const cell = cellValues.find(c => c.id == cellId && c.layerId == layerId)

		if (cell && cell.getValue() != value)
			cell.setValue(value)
	}

	/**
	 *
	 * @param  {Object}	 cellValues
	 * @param  {Cell}	 data.cell
	 * @param  {Object}	 data.value
	 * @param  {String}	 data.layer.id
	 * @param  {String}	 data.layer.component
	 * @param  {String}	 data.name				Field name
	 * @param  {String}	 data.type				Field type, e.g., 'textfield', 'slider'
	 * 
	 * @return {Void}					
	 */
	const mutateCellValues = (cellValues, data) => {
		if (!data || !data.type || !data.cell || !data.cell.id || !data.layer)
			return 
		
		if (data.type == 'radio') 
			mutateRadioCellValues(cellValues, data.cell.id, data.layer.id, data.value, data.name)
		else 
			mutateStandardCellValues(cellValues, data.cell.id, data.layer.id, data.value, data.name)
	}

	const setSelectedCellIds = ids => selectedCellIds = ids 


	/**
	 * Creates a new Cell object. 
	 * 
	 * @param  {String} id					Optional's cell's ID. Default is randomly generated.
	 * @param  {Number} width				Default 100
	 * @param  {String} widthUnit			Default is '%'. Valid values: 'px', '%'
	 * @param  {Object} layers				e.g., [{ id:0, name:'layer_0', toggled:true, component:{ id:'textfield', type:'native' } }]
	 * @param  {Object} component			If 'layers' is not specified, then this component is used to create one. 
	 *                                   	e.g., { id:'textfield', type:'native' }	
	 *                                   	
	 * @return  {String}  id					
	 * @return  {Number}  width				
	 * @return  {String}  widthUnit		
	 * @return  {String}  defaultLayerId	
	 * @return  {[Layer]} layers			
	 */
	const createCell = ({ id, width, widthUnit, layers, component, aspectRatio, backgroundColor }) => {
		const _layers = layers
			? Array.isArray(layers) ? layers : [layers]
			: (component ? [{ id:0, name:'layer_0', toggled:true, component }] : null)

		const defaultLayerId = _layers ? _layers[0].id : null
		return {
			id: id||getId(),
			width: width||100,
			widthUnit:widthUnit||'%',
			aspectRatio, 
			backgroundColor,
			defaultLayerId,
			layers: _layers
		}
	}

	const createRow = ({ id, height, cells }) => {
		id = id || getId()
		height = height || null 
		return { id, height, cells:cells||[] }
	}

	/**
	 * Adds new cells in a row. 
	 * 
	 * @param  {String}  rowCells[].id
	 * @param  {Boolean} newCells[].id					Optional. Default is auto-generated.
	 * @param  {Number}  newCells[].width
	 * @param  {String}  newCells[].widthUnit
	 * @param  {String}  newCells[].neighbourId
	 * @param  {String}  newCells[].neighbourSide
	 * @param  {Number}  newCells[].layers[].id
	 * @param  {Boolean} newCells[].layers[].toggled
	 * @param  {Number}  newCells[].layers[].component
	 * 
	 * @return {[Cell]} cells
	 */
	const addNewCellsToRow = (rowCells, newCells, index=0, lastIndex, cellCounter) => {
		if (index === 0) {
			cellCounter = rowCells.length
			lastIndex = cellCounter-1
			if (!newCells || !newCells.length)
				return rowCells
		}

		const cell = rowCells[index]
		const leftSibling = newCells.find(c => c.neighbourId == cell.id && c.neighbourSide == 'left')
		const rightSibling = newCells.find(c => c.neighbourId == cell.id && c.neighbourSide == 'right')
		if (leftSibling)
			cellCounter++
		if (rightSibling)
			cellCounter++

		let results
		if (index == lastIndex) {
			const newRowCells = new Array(cellCounter) 
			results = { cells:newRowCells, cursor:--cellCounter }
		} else
			results = addNewCellsToRow(rowCells, newCells, index+1, lastIndex, cellCounter)
	

		if (rightSibling)
			results.cells[results.cursor--] = createCell({ 
				id: rightSibling.id,
				width:rightSibling.width, 
				widthUnit:rightSibling.widthUnit, 
				layers:rightSibling.layers,
				aspectRatio:rightSibling.aspectRatio,
				backgroundColor:rightSibling.backgroundColor 
			})

		results.cells[results.cursor--] = cell
		
		if (leftSibling)
			results.cells[results.cursor--] = createCell({ 
				id: leftSibling.id,
				width:leftSibling.width, 
				widthUnit:leftSibling.widthUnit, 
				layers:leftSibling.layers,
				aspectRatio:leftSibling.aspectRatio,
				backgroundColor:leftSibling.backgroundColor 
			})

		return index == 0 ? results.cells : results
	}

	const getNewRowCount = newCells => {
		if (!newCells || !newCells[0])
			return 0

		return newCells.reduce((acc,c) => {
			if (c && c.newCell) {
				if (exists(c.newCell.belowRowIndex))
					acc.add(c.newCell.belowRowIndex+1)
				else if (exists(c.newCell.aboveRowIndex))
					acc.add(c.newCell.aboveRowIndex-1)
			}
			return acc 
		}, new Set()).size
	}

	/**
	 * Recursively update each cell in each row
	 * 
	 * @param  {[Row]} 		rows        
	 * @param  {Function} 	processCell 
	 * @param  {String} 	addCells.cells[].id
	 * @param  {Object} 	addCells.cells[].cell
	 * @param  {Number} 	addCells.cells[].rowIndex				
	 * @param  {Number} 	addCells.cells[].belowRowIndex			
	 * @param  {Number} 	addCells.cells[].aboveRowIndex			
	 * @param  {String} 	addCells.cells[].neighbourId			
	 * @param  {String} 	addCells.cells[].neighbourSide			 
	 * @param  {Number} 	addCells.cursor
	 * @param  {Boolean} 	newCells[].id						Optional. Default is auto-generated.
	 * @param  {Number} 	newCells[].width					Optional. Default is adjusted to fit.
	 * @param  {String} 	newCells[].widthUnit				Optional. Default is adjusted to fit.
	 * @param  {Boolean} 	newCells[].newCell.singleCellRow	If true, only 'belowRowIndex' or 'aboveRowIndex' are valid.
	 * @param  {Number} 	newCells[].newCell.rowIndex			Means the cell must be moved inside this row index.
	 * @param  {Number} 	newCells[].newCell.belowRowIndex	Means the cell must be moved below this row index in a new row.
	 * @param  {Number} 	newCells[].newCell.aboveRowIndex	Means the cell must be moved above this row index in a new row.
	 * @param  {String} 	newCells[].newCell.neighbourId		Only valid when 'rowIndex' is set.
	 * @param  {String} 	newCells[].newCell.neighbourSide	Only valid when 'rowIndex' is set. Values: 'left' or 'right'. 
	 * @param  {String} 	newCells[].newCell.backgroundColor
	 * @param  {String} 	newCells[].newCell.aspectRatio
	 * @param  {String} 	newCells[].newCell.layers[].id
	 * @param  {Boolean} 	newCells[].newCell.layers[].toggled
	 * @param  {Object} 	newCells[].newCell.layers[].component
	 * @param  {Boolean}	activeBreakpoint
	 * @param  {Number} 	index       						Row's index
	 * @param  {[type]} 	lastIndex   						Row's last index
	 * @param  {Number} 	rowCounter  						Current amount of valid row (invalid means it does not exist anymore)
	 * 
	 * @return {[Row]}		newRows
	 */
	const updateGridRows = (rows, processCell, addCells, newCells, activeBreakpoint, index=0, lastIndex, rowCounter=0) => {
		if (index === 0) {
			const l = rows.length
			lastIndex = l-1
		}

		const row = rows[index]
		const rowCells = row.cells
		const l = rowCells.length
		const newRowCells = []
		let mutated = false, skipCount=0, movedCellsAdded=false
		for (let i=0;i<l;i++) {
			const cell = rowCells[i]
			const c = processCell(cell, index, i, lastIndex, addCells)
			if (c === null) {
				skipCount++
				mutated = true
			} else if (c) {
				mutated = true
				const [leftCell, mutatedCell, rightCell] = c 
				if (leftCell) {
					movedCellsAdded = true
					newRowCells.push(leftCell)	
				}
				newRowCells.push(mutatedCell)
				if (rightCell) {
					movedCellsAdded = true
					newRowCells.push(rightCell)	
				}	
			} else 
				newRowCells.push(cell)
		}

		const mutatedRow = mutated ? (skipCount == l ? null : { ...row, cells: newRowCells }) : row

		if (mutatedRow)
			rowCounter++

		let results = null
		if (index == lastIndex) {

			const newRowsCount = newCells 
				? activeBreakpoint 
					? getNewRowCount(newCells)
					: newCells.length // When the breakpoint is not the active one, then the cell is added at the bottom in it own row
				: 0
			const newMovedRowsCount = addCells && addCells.cells 
				? addCells.cells.filter(x => exists(x.aboveRowIndex) || exists(x.belowRowIndex)).length
				: 0

			// 1. Create the rows array
			const newSize = rowCounter+newRowsCount+newMovedRowsCount

			results = { 
				newRows: new Array(newSize),
				indexCursor:newSize-1, 
				cellsHaveMoved:addCells&&addCells.cells.length>0 
			}

			// 2. If this grid is not the active breakpoint, and if there are new cells, then add all of them in their
			// own row at the bottom.
			if (newRowsCount && !activeBreakpoint)
				for (let k=0;k<newRowsCount;k++) {
					const nc = newCells[k]
					results.newRows[results.indexCursor--] = createRow({ cells:[createCell({ id:nc.id, layers:nc.newCell.layers })] })
				}
		} else
			results = updateGridRows(rows, processCell, addCells, newCells, activeBreakpoint, index+1, lastIndex, rowCounter)

		// 1. Gets the new rows that must be added above or below this row due to a cell move
		const extraRowsDueToCellMove = results.cellsHaveMoved 
			? addCells.cells.reduce((acc,c) => {
				if (c.belowRowIndex == index)
					acc.rowCellsBelow = [c.cell]
				if (c.aboveRowIndex == index)
					acc.rowCellsAbove = [c.cell]
				
				return acc 
			}, { rowCellsBelow:null, rowCellsAbove:null })
			: { rowCellsBelow:null, rowCellsAbove:null }

		// 2. Gets the new rows that must be added above or below this row due to a new cell being created
		const extraRowsDueToNewCell = !activeBreakpoint || !newCells 
			? { rowCellsBelow:[], rowCellsAbove:[], newCellsInThisRow:[] } 
			: newCells.reduce((acc,c) => {
				if (c.newCell.belowRowIndex == index)
					acc.rowCellsBelow.push(createCell({ 
						id:c.id, 
						width:c.newCell.width, 
						widthUnit:c.newCell.widthUnit, 
						layers:c.newCell.layers,
						aspectRatio:c.newCell.aspectRatio,
						backgroundColor:c.newCell.backgroundColor 
					}))
				else if (c.newCell.aboveRowIndex == index)
					acc.rowCellsAbove.push(createCell({ 
						id:c.id, 
						width:c.newCell.width, 
						widthUnit:c.newCell.widthUnit, 
						layers:c.newCell.layers,
						aspectRatio:c.newCell.aspectRatio,
						backgroundColor:c.newCell.backgroundColor 
					}))
				else if (c.newCell.rowIndex == index && mutatedRow && mutatedRow.cells.some(cc => cc && cc.id == c.newCell.neighbourId))
					acc.newCellsInThisRow.push({ ...c.newCell, id:c.id })

				return acc
			}, { rowCellsBelow:[], rowCellsAbove:[], newCellsInThisRow:[] })

		// 3. Add row below
		if (extraRowsDueToCellMove.rowCellsBelow)
			results.newRows[results.indexCursor--] = createRow({ cells:extraRowsDueToCellMove.rowCellsBelow })
		if (extraRowsDueToNewCell.rowCellsBelow[0]) 
			results.newRows[results.indexCursor--] = createRow({ cells:extraRowsDueToNewCell.rowCellsBelow })

		// 4. Add current row
		if (mutatedRow) {
			// 4.1. Add the moved cells in this row
			let mutatedRowCells = movedCellsAdded 
				? mutatedRow.cells.map(cell => {
					if (cell.fake)
						return addCells.cells.find(c => c && c.cell.id == cell.id).cell
					else 
						return cell
				})
				: mutatedRow.cells
			// 4.2. Add the new cells in this row
			const mustAddNewCells = extraRowsDueToNewCell.newCellsInThisRow[0]
			if (mustAddNewCells) {
				const newFlexCellsCount = extraRowsDueToNewCell.newCellsInThisRow.filter(c => c.widthUnit != 'px').length
				const totalFlexCellsCount = mutatedRowCells.filter(c => c.widthUnit == '%').length + newFlexCellsCount

				const newCellWidth = ~~(100/totalFlexCellsCount)
				const additionalFlexWidth = newFlexCellsCount*newCellWidth

				mutatedRowCells = addNewCellsToRow(
					adjustRowCellsWidth(mutatedRowCells, { additionalFlexWidth }).cells, 
					extraRowsDueToNewCell.newCellsInThisRow.map(c => { 
						if (c.width && c.widthUnit == 'px')
							return c 
						else
							return { ...c, width:newCellWidth, widthUnit:'%' }
					})
				)
			}

			results.newRows[results.indexCursor--] = !movedCellsAdded && !mustAddNewCells 
				? mutatedRow 
				: { ...mutatedRow, cells: mutatedRowCells }
		}

		// 5. Add row above
		if (extraRowsDueToCellMove.rowCellsAbove)
			results.newRows[results.indexCursor--] = createRow({ cells: extraRowsDueToCellMove.rowCellsAbove })
		if (extraRowsDueToNewCell.rowCellsAbove[0])
			results.newRows[results.indexCursor--] = createRow({ cells: extraRowsDueToNewCell.rowCellsAbove })

		return index == 0 ? results.newRows : results
	}

	const cloneLayer = (layer, newId) => {
		if (!layer)
			return layer 

		const clone = { ...layer }
		if (clone.component)
			clone.component = { ...layer.component }

		if (newId !== undefined)
			clone.id = newId

		return clone 
	}

	const cloneCell = (cell, newId) => {
		if (!cell)
			return cell 

		const clone = { ...cell }
		if (clone.layers)
			clone.layers = cell.layers.map(l => cloneLayer(l))

		if (newId !== undefined)
			clone.id = newId		

		return clone 
	}

	/**
	 * 
	 * @param  {Grid} 		grid				
	 * @param  {Function} 	rowMutations[].id						Row's ID (only valid for existing row)
	 * @param  {Function} 	rowMutations[].updateSize		
	 * @param  {Row} 		rowMutations[].newRow.row
	 * @param  {Number} 	rowMutations[].newRow.aboveRowIndex	
	 * @param  {Number} 	rowMutations[].newRow.belowRowIndex	
	 * 
	 * @return {Grid}
	 */
	const updateRowsGrid = (grid, rowMutations) => {
		const newGrid = { ...grid }
		const rl = grid.rows.length
		const extraRowCount = rowMutations.filter(x => x.newRow).length
		newGrid.rows = new Array(rl+extraRowCount)
		let offset = 0
		for(let i=0;i<rl;i++) {
			const row = grid.rows[i]
			const { rowsAbove, rowsBelow } = rowMutations.reduce((acc,x) => {
				if (x.newRow && x.newRow.row) {
					if (x.newRow.aboveRowIndex == i)
						acc.rowsAbove.push(x.newRow.row)
					else if (x.newRow.belowRowIndex == i)
						acc.rowsBelow.push(x.newRow.row)
				}
				return acc 
			}, { rowsAbove:[], rowsBelow:[] })

			// Adds new rows above
			if (rowsAbove[0])
				for (let j=0;j<rowsAbove.length;j++) {
					const newRow = {...rowsAbove[j]}
					newRow.id = getId()
					newRow.cells = newRow.cells.map(c => cloneCell(c, getId()))
					newGrid.rows[i+offset] = newRow
					offset++
				}

			// Updates current row
			const mutation = rowMutations.find(r => r.id == row.id && r.updateSize)
			newGrid.rows[i+offset] = mutation ? mutation.updateSize(row) : row

			// Adds new rows below
			if (rowsBelow[0]) 
				for (let j=0;j<rowsBelow.length;j++) {
					const newRow = {...rowsBelow[j]}
					newRow.id = getId()
					newRow.cells = newRow.cells.map(c => cloneCell(c, getId()))
					newGrid.rows[i+1+offset] = newRow
					offset++
				}
		}

		return newGrid
	}

	/**
	 * Updates grid
	 * 
	 * @param  {Number} 	grid.viewportWidth 
	 * @param  {[[Cell]]} 	grid.rows 				
	 * @param  {[String]} 	removeCellIds
	 * @param  {String} 	moveCells[].id
	 * @param  {Number} 	moveCells[].moveCell.rowIndex				Means the cell must be moved inside this row index.
	 * @param  {Number} 	moveCells[].moveCell.belowRowIndex			Means the cell must be moved below this row index in a new row.
	 * @param  {Number} 	moveCells[].moveCell.aboveRowIndex			Means the cell must be moved above this row index in a new row.
	 * @param  {String} 	moveCells[].moveCell.neighbourId			Only valid when 'rowIndex' is set.
	 * @param  {String} 	moveCells[].moveCell.neighbourSide			Only valid when 'rowIndex' is set. Values: 'left' or 'right'. 
	 * @param  {String} 	updateSizeCells[].id
	 * @param  {Function} 	updateSizeCells[].updateSize
	 * @param  {Boolean} 	newCells[].id								Optional. Default is auto-generated.
	 * @param  {Number} 	newCells[].width							Optional. Default is adjusted to fit.
	 * @param  {String} 	newCells[].widthUnit						Optional. Default is adjusted to fit.
	 * @param  {Boolean} 	newCells[].newCell.singleCellRow			If true, only 'belowRowIndex' or 'aboveRowIndex' are valid.
	 * @param  {Number} 	newCells[].newCell.rowIndex					Means the cell must be moved inside this row index.
	 * @param  {Number} 	newCells[].newCell.belowRowIndex			Means the cell must be moved below this row index in a new row.
	 * @param  {Number} 	newCells[].newCell.aboveRowIndex			Means the cell must be moved above this row index in a new row.
	 * @param  {String} 	newCells[].newCell.neighbourId				Only valid when 'rowIndex' is set.
	 * @param  {String} 	newCells[].newCell.neighbourSide			Only valid when 'rowIndex' is set. Values: 'left' or 'right'. 
	 * @param  {String} 	newCells[].newCell.layers[].id
	 * @param  {Boolean} 	newCells[].newCell.layers[].toggled
	 * @param  {Object} 	newCells[].newCell.layers[].component
	 * @param  {Boolean}	activeBreakpoint
	 * 
	 * @return {Grid}		updatedGrid
	 */
	const updateCellsGrid = (grid, removeCellIds, moveCells, updateSizeCells, newCells, activeBreakpoint) => {
		const newGrid = { ...grid }

		const addCells = moveCells === null 
			? null 
			: { cells:new Array(moveCells.length), cursor:0 }

		/**
		 * Processes the cell. 
		 * 
		 * @param  {Cell}	cell.id
		 * @param  {Number}	cell.width
		 * @param  {String}	cell.widthUnit
		 * @param  {Object}	cell.layers
		 * @param  {Number} rowIndex
		 * @param  {Number} cellIndex
		 * @param  {Number} lastRowIndex
		 * @param  {String} addCells.cells[].id
		 * @param  {Object} addCells.cells[].cell
		 * @param  {Number} addCells.cells[].rowIndex				
		 * @param  {Number} addCells.cells[].belowRowIndex			
		 * @param  {Number} addCells.cells[].aboveRowIndex			
		 * @param  {String} addCells.cells[].neighbourId			
		 * @param  {String} addCells.cells[].neighbourSide			 
		 * @param  {Number} addCells.cursor
		 * 
		 * @return {Object} output				* NULL means the cell must be deleted and its row can be deleted is empty.
		 *                            			* false means that the cell has not been mutated.
		 *                            			* [newLeftCellRef, mutatedCell, newRightCellRef] is the mutated cell
		 */
		const processCell = (cell, rowIndex, cellIndex, lastRowIndex, addCells) => {
			if (removeCellIds !== null && removeCellIds.includes(cell.id))
				return null

			let foundMatch = false, updatedCell = cell
			if (updateSizeCells !== null) {
				const updateCell = updateSizeCells.find(u => u.id == cell.id)
				if (updateCell) {
					foundMatch = true
					const c = updateCell.updateSize(updatedCell)
					if (c != cell) // this helps mutating 'updatedCell' to prevent re-rendering
						updatedCell = c
				}
			}

			if (moveCells !== null) {
				const moveCell = moveCells.find(u => u.id == cell.id)
				if (moveCell) {
					addCells.cells[addCells.cursor++] = { cell:updatedCell, ...moveCell.moveCell }
					return null
				}

				const [n1,n2] = moveCells.filter(c => c && c.moveCell && c.moveCell.neighbourId == cell.id)
				if (n1 || n2) {
					const leftCell = n1 && n1.moveCell.neighbourSide == 'left' 
						? { fake:true, id:n1.id } 
						: n2 && n2.moveCell.neighbourSide == 'left' ? { fake:true, id:n2.id } : null
					const rightCell = n1 && n1.moveCell.neighbourSide == 'right' 
						? { fake:true, id:n1.id } 
						: n2 && n2.moveCell.neighbourSide == 'right' ? { fake:true, id:n2.id } : null

					return [leftCell, updatedCell == cell ? cell : updatedCell, rightCell]
				}
			}

			return foundMatch ? [null, updatedCell == cell ? cell : updatedCell, null] : false
		}

		newGrid.rows = updateGridRows(newGrid.rows, processCell, addCells, newCells, activeBreakpoint)

		return newGrid
	}

	/**
	 * Create a new 'grids' array from the current 'grids' which contains the changes defined
	 * in 'cellMutations'. Emit an 'gridschange' event containing this new array so the parent can
	 * mutate the original 'cells' array.
	 *
	 * NOTES:
	 * 	- By default, the changes only apply to the current viewport.
	 * 	- Deleting a cell applies to all breakpoints, even if the options.allViewports is not set to true.
	 *
	 * 
	 * 
	 * @param  {String} 	cellMutations[].id							Cell's ID to mutate. 
	 * @param  {Number} 	cellMutations[].moveCell.rowIndex
	 * @param  {Number} 	cellMutations[].moveCell.neighbourId
	 * @param  {String} 	cellMutations[].moveCell.neighbourSide
	 * @param  {Number} 	cellMutations[].moveCell.aboveRowIndex
	 * @param  {Number} 	cellMutations[].moveCell.belowRowIndex             
	 * @param  {Boolean} 	cellMutations[].newCell.id					Optional. Default is auto-generated.
	 * @param  {Number} 	cellMutations[].newCell.width				Optional. Default is adjusted to fit.
	 * @param  {String} 	cellMutations[].newCell.widthUnit			Optional. Default is adjusted to fit.
	 * @param  {Boolean} 	cellMutations[].newCell.singleCellRow		If true, only 'belowRowIndex' or 'aboveRowIndex' are valid.
	 * @param  {Number} 	cellMutations[].newCell.rowIndex			Means the cell must be moved inside this row index.
	 * @param  {Number} 	cellMutations[].newCell.belowRowIndex		Means the cell must be moved below this row index in a new row.
	 * @param  {Number} 	cellMutations[].newCell.aboveRowIndex		Means the cell must be moved above this row index in a new row.
	 * @param  {String} 	cellMutations[].newCell.neighbourId			Only valid when 'rowIndex' is set.
	 * @param  {String} 	cellMutations[].newCell.neighbourSide		Only valid when 'rowIndex' is set. Values: 'left' or 'right'.
	 * @param  {String} 	cellMutations[].newCell.backgroundColor
	 * @param  {String} 	cellMutations[].newCell.aspectRatio
	 * @param  {String} 	cellMutations[].newCell.layers[].id
	 * @param  {Boolean} 	cellMutations[].newCell.layers[].toggled
	 * @param  {Object} 	cellMutations[].newCell.layers[].component
	 * @param  {Function} 	cellMutations[].updateSize					cell => mutatedCell	
	 * @param  {Boolean} 	cellMutations[].delete						If true, the cell is deleted.
	 * @param  {Function} 	rowMutations[].updateSize					row => mutatedRow
	 * @param  {Function} 	rowMutations[].newRow						row => mutatedRow
	 * @param  {Boolean} 	options.allViewports						Default false. If true, the update is applied to all breakpoints.
	 * @param  {Number} 	options.viewportWidth						Default is variable currentViewportWidth	
	 * @param  {String} 	options.caller								Used for debugging purposes
	 * 
	 * @return {Void}
	 */
	const udpateGrids = (cellMutations, rowMutations, options) => {
		if (!edit)
			return

		options = options || {}
		const { allViewports=false, viewportWidth:activeViewportWidth=currentViewportWidth } = options

		// console.log(`udpateGrids(${id}): ${options.caller}`)

		const { removeCellIds, moveCells, newCells, updateSizeCells } = (cellMutations||[]).reduce((acc,c) => {
			if (c) {
				if (c.id) {
					if (c.delete)
						acc.removeCellIds.push(c.id)
					if (c.moveCell)
						acc.moveCells.push(c)
					if (c.updateSize)
						acc.updateSizeCells.push(c)
				} 
				if (c.newCell)
					acc.newCells.push(c)
			}
			return acc
		}, { removeCellIds:[], moveCells:[], newCells:[], updateSizeCells:[] })
		
		const someCellsMustBeDeleted = removeCellIds[0]
		const someCellsMustBeAdded = newCells[0]

		const newGrids = (grids||[]).map(g => {
			const activeBreakpoint = g.viewportWidth == activeViewportWidth
			const vpMatches = allViewports || activeBreakpoint
			const gridMutationOn = someCellsMustBeDeleted || someCellsMustBeAdded || vpMatches
			let grid = gridMutationOn
				? updateCellsGrid(
					g, 
					someCellsMustBeDeleted ? removeCellIds : null,
					vpMatches && moveCells[0] ? moveCells : null,
					vpMatches && updateSizeCells[0] ? updateSizeCells : null,
					someCellsMustBeAdded ? newCells : null,
					activeBreakpoint) 
				: g

			if (gridMutationOn && rowMutations && rowMutations[0])
				grid = updateRowsGrid(grid, rowMutations)	

			return grid 
		})

		// console.log({updatedCells})

		emitGridsChange({ grids:newGrids })
	}

	/**
	 * Deletes cell ids in all viewports. 
	 * 
	 * @param  {[String]} 	ids
	 * 
	 * @return {Void}		
	 */
	const deleteCellIds = ids => {
		if (!ids || !ids.length)
			return

		const l = grids.length
		const newGrids = new Array(l)
		const options = { removeCellIds:ids }

		for (let i=0;i<l;i++) {
			const grid = grids[i]
			const newGrid = { ...grid }
			newGrid.rows = map(
				grid.rows, 
				row => {
					const { cells:newCells } = adjustRowCellsWidth(row.cells, options)
					const newL = newCells.length
					if (newL == 0)
						return null
					else if (newL == row.cells.length)
						return row
					else 
						return { ...row, cells:newCells }
				},
				true)

			newGrids[i] = newGrid
		}

		emitGridsChange({ grids:newGrids })
	}

	/**
	 * Gets all the values stored into 'cellValues' store into a 'data' object and emit a 'componentclick' event.
	 *
	 * @param  {Cell}    input.cell					
	 * @param  {String}	 input.layer
	 * @param  {Boolean} options.formData			Default false. When true, the data are submit in a FormData object instead of JSON.
	 * 
	 * @return {Object}  data
	 */
	const assembleAllValuesAndEmitClick = (input, options) => {
		if (!cellValues.length)
			return
		
		const { formData } = options||{}

		let data = cellValues.reduce((acc,d,i) => {
			if (d.type == 'text' || d.type == 'button')
				return acc 

			const fieldName = d.name || `${(d.type||'field')}_${i}`
			const val = d.getValue()
			if (d.type == 'radio') {
				if (val)
					acc[fieldName] = val
			} else if (d.type == 'checkbox') {
				if (!acc[fieldName])
					acc[fieldName] = []
				acc[fieldName].push(val)
			} else 
				acc[fieldName] = val
			return acc
		}, {})

		if (formData)
			data = Object.keys(data).reduce((form,prop) => {
				form.append(prop, data[prop])
				return form 
			}, new UFormData())

		emitComponentClick({ ...input, data })

		return data
	}

	/**
	 * Mutates the 'grids' and 'toggledCellLayers' based on the combination of the 'layer.triggers' logic and the 
	 * 'value'.
	 * 
	 * @param  {Cell}   cell				
	 * @param  {Object} layer
	 * @param  {Object} value
	 * @param  {String} event	e.g., 'click', 'change'				
	 * 
	 * @return {Void}
	 */
	const executeTriggers = async (cell, layer, value, event) => {
		const [newGrids, newToggledCellLayers] = await processTriggers(
			grids, 
			currentViewportWidth, 
			cell, 
			layer, 
			value, 
			toggledCellLayers, 
			event)

		if (newGrids && grids != newGrids)
			grids = newGrids

		if (newToggledCellLayers && newToggledCellLayers !== toggledCellLayers)
			toggledCellLayers = newToggledCellLayers
	}

	/**
	 * Updates the local store containing the cell's values. 
	 * 
	 * @param  {[type]} cellIds [description]
	 * @return {[type]}         [description]
	 */
	const deleteCellIdsValues = cellIds => {
		if (cellIds && cellIds.length && cellValues.length) 
			cellValues = cellValues.filter(c => cellIds.includes(c.id))
	}

	/**
	 * Deletes a cell with maybe all selected cells. 
	 * 
	 * @param  {Boolean} data.selected	If true, also include all selected cells (i.e., 'selectedCellIds'), otherwise only data.id. 
	 * 
	 * @return {Void}	
	 */
	const deleteCells = (data) => {
		if (data && data.selected) {
			const cellIds = data.id ? Array.from(new Set([data.id, ...selectedCellIds])) : selectedCellIds
			const l = cellIds ? cellIds.length : 0
			if (l) {
				deleteCellIdsValues(cellIds)
				const cellToSelectAfterDelete = getPreviousCell(cellIds)
				tick().then(() => {
					deleteCellIds(cellIds)
					tick().then(() => {
						if (cellToSelectAfterDelete)
							selectCell(cellToSelectAfterDelete, getToggledLayers(cellToSelectAfterDelete))
					})
				})
			}
		} else if (data && data.id) {
			deleteCellIdsValues([data.id])
			deleteCellIds([data.id], data.backup)
		}
	}

	/**
	 * Moves an existing cell into a specific row. Adjust the existing row cells' width to accomodate for the 
	 * insertion of that existing cell. Also update the existing cell's rowSeq.
	 * 
	 * @param  {String}  cell.id					
	 * @param  {Number}  cell.width
	 * @param  {String}  cell.widthUnit
	 * 
	 * @param  {Number}  from.rowIndex			
	 * @param  {Number}  from.cellIndex
	 * @param  {Boolean} from.singleCellRow		
	 * @param  {[Cell]}  from.rowCells
	 * 
	 * @param  {Number}  to.rowIndex					
	 * @param  {Boolean} to.singleCellRow		
	 * @param  {String}  to.neighbourId				
	 * @param  {String}  to.neighbourSide				
	 * @param  {Number}  to.belowRowIndex		Position where the cell should be placed.
	 * @param  {Number}  to.aboveRowIndex		Position where the cell should be placed.
	 * 
	 * @return {Void}
	 */
	const moveCellToRow = (cell, from, to) => {
		// Use case 1: Same row cell move. In this case, the only mutation occurs on the cells order in this row.
		if (from.rowIndex === to.rowIndex)
			udpateGrids([{
				id:cell.id,
				moveCell: to
			}], null, { caller:'moveCellToRow:same_row' })
		// Use case 2: The cell is moved to another row.
		else {
			const newRowMustBeCreatedForThisCell = exists(to.belowRowIndex) || exists(to.aboveRowIndex)
			const toRowCells = rows[to.rowIndex||0].cells
			const l = toRowCells.length
			const newCell = { id:cell.id, width: cell.width, widthUnit: cell.widthUnit }
			const flexWidth = newCell.widthUnit == '%'
			if (flexWidth)
				// Adjust the cell's width if it is too big.
				newCell.width = Math.min(cell.width,~~100/(l+1))
			
			// Readjust the cells' width in the 'from' row to claim the space left by cell that is leaving.
			const fromRowMutations = from.singleCellRow 
				? [] 
				: adjustRowCellsWidth(from.rowCells, { removeCellIds:[cell.id] }).cells.map(c => ({
					id: c.id,
					updateSize: cl => {
						// if (cl.width < 0)
						// 	console.log(`Cell ${cl.id} (${cl.width}${cl.widthUnit}) - moveCellToRow 01`)
						return ({ ...cl, width: cl.width, widthUnit: cl.widthUnit })
					}
				}))

			// Readjust the cells' width in the 'to' row to give some space to the cell that is coming.
			const adjustedToCells = newRowMustBeCreatedForThisCell
				? { adjustedAdditionalFlexWidth:0 }
				: adjustRowCellsWidth(toRowCells, { additionalFlexWidth:flexWidth ? newCell.width : null })

			const toRowMutations = newRowMustBeCreatedForThisCell
				? []
				: adjustedToCells.cells.map(c => ({
					id: c.id,
					updateSize: cl => {
						// if (c.width < 0)
						// 	console.log(`Cell ${cl.id} (${c.width}${cl.widthUnit}) - moveCellToRow 02`)
						return ({ ...cl, width:c.width })
					}
				}))

			// Move the cell
			const cellMutation = newRowMustBeCreatedForThisCell 
				? {
					id:cell.id,
					moveCell: {
						belowRowIndex:to.belowRowIndex,
						aboveRowIndex:to.aboveRowIndex
					}
				}
				: {
					id:cell.id,
					moveCell: to,
					updateSize: cl => {
						const newWidth = adjustedToCells.adjustedAdditionalFlexWidth || newCell.width
						// if (newWidth < 0)
						// 	console.log(`Cell ${cl.id} (${newWidth}${cl.widthUnit}) - moveCellToRow 03`)
						return ({ ...cl, width:newWidth }) 
					}
				}

			udpateGrids([
				...fromRowMutations,
				cellMutation,
				...toRowMutations
			], null, { caller:'moveCellToRow:diff_row' })
		}

		emitCellMove({ id:cell.id, to })
		tick().then(() => setSelectedCellIds([cell.id]))
	}

	/**
	 * Gets the current windows viewport width. This function exists because the svelte:window component might not be
	 * ready when this component starts, which means that the viewport local variable will be undefined.
	 * 
	 * @param  {[type]} viewportWidth 
	 * @return {[type]}          
	 */
	const getViewportWidth = viewportWidth => viewportWidth || WIN.innerWidth || 1400

	const getNextRowCell = (nextRowIndex, currentCellRelativePos) => {
		const rowCells = rows[nextRowIndex].cells
		const l = rowCells ? rowCells.length : 0
		if (l <= 1)
			return 0 

		return ~~(currentCellRelativePos*(l-1))
	}

	const getNextCellId = ({ direction, cellIndex, rowIndex }) => {
		const maxRowIndex = rows.length - 1
		const maxCellIndex = rows[rowIndex].cells.length - 1
		const verticalMove = maxRowIndex > 0 && (direction == 'up' || direction == 'down')
		const horizontalMove = maxCellIndex > 0 && (direction == 'left' || direction == 'right')

		if (!verticalMove && !horizontalMove)
			return null

		const nextRowIndex = verticalMove 
			? direction == 'up' 
				? rowIndex == 0 ? maxRowIndex : rowIndex-1 
				: rowIndex == maxRowIndex ? 0 : rowIndex+1
			: rowIndex

		const nextCellIndex = verticalMove 
			? cellIndex == 0 || maxCellIndex == 0 ? 0 : getNextRowCell(nextRowIndex, (cellIndex+1)/(maxCellIndex+1))
			: direction == 'left'
				? cellIndex == 0 ? maxCellIndex : cellIndex-1
				: cellIndex == maxCellIndex ? 0 : cellIndex+1 

		return rows[nextRowIndex].cells[nextCellIndex].id
	}

	/**
	 * Gets the first cell on the left. If there is none, then try the last cell on the row above. If there is 
	 * no row above, try the last cell on the last row. 
	 * 
	 * @param  {String} cellId
	 * 
	 * @return {Cell}	previousCell
	 */
	const getPreviousCell = cellIds => {
		if (!cellIds || !cellIds[0])
			return null 

		let previousCell = null, cursor
		for (let i=0,l=rows.length;i<l;i++) {
			const rowCells = rows[i].cells
			for (let j=0,k=rowCells.length;j<k;j++) {
				const cell = rowCells[j]
				const match = cellIds.includes(cell.id)
				if (match && cursor) {
					previousCell = cursor
					break
				} else if (!match)
					cursor = cell
			}
			if (previousCell)
				break
		}

		return previousCell || cursor
	}

	/**
	 * Mutates the 'selectedCellIds' property and emit the 'cellselect' event.
	 * 
	 * @param  {Cell}  		cell
	 * @param  {[Layer]}	layers
	 * 
	 * @return {Void}
	 */
	const selectCell = (cell, layers) => {
		if (selectedCellIds.length == 1 && selectedCellIds.includes(cell.id))
			return 

		if (commandControlKeyDown)
			setSelectedCellIds(Array.from(new Set([...selectedCellIds,cell.id])))
		else
			setSelectedCellIds([cell.id])

		emitCellSelect({ cell, layers, selectedCellIds })
	}

	const getEdgeThickness = (...refs) => Math.min(EDGE_THICKNESS, ...refs.map(r => Math.ceil(r/4)))

	/**
	 * Gets the cell that contains the coordinates. If no cell matches, return which new cell could.
	 * 
	 * @param  {Number} x
	 * @param  {Number} y
	 * 
	 * @return {Object}  match.cell					Matching cell. Null means no cell could match.
	 * @return {String}  match.cell.id				Cell's ID.
	 * @return {Object}  match.cell.horizontalZone	e.g., 'left' or 'right'
	 * @return {Boolean} match.cell.horizontalEdge	true means the cursor if on the top or bottom edge of the cell.
	 * @return {Object}  match.cell.verticalZone	e.g., 'top', or 'bottom'
	 * @return {Boolean} match.cell.verticalEdge	true means the cursor if on the left or right edge of the cell.
	 * @return {Object}  match.virtualPos			e.g., { rowIndex:1, side:'left', pos:'in' } means new cell would be on 
	 *                                   	 		the left of row index 1.
	 *                                	   	  		{ rowIndex:0, side:'left', pos:'above' } means new cell would be above row index 0.
	 */
	const findCellHoveredOn = (x,y) => {
		const results = cellDimensions.reduce((acc,c) => {
			if (!acc.match) {
				const dim = c.getDim(true) 
				const isInSameRow = dim.y <= y && y < dim.bottom 
				const isInside = dim.x <= x && x < dim.right && isInSameRow
				if (isInside) {
					const horizontalEdgeThickness = getEdgeThickness(dim.height)
					const verticalEdgeThickness = getEdgeThickness(dim.width)
					dim.id = c.id
					dim.verticalEdge = x <= dim.x + verticalEdgeThickness || x >= dim.right - verticalEdgeThickness
					dim.horizontalZone = x <= dim.x + dim.width/2 ? 'left' : 'right'
					dim.horizontalEdge = y <= dim.y + horizontalEdgeThickness || y >= dim.bottom - horizontalEdgeThickness
					dim.verticalZone = y <= dim.y + dim.height/2 ? 'top' : 'bottom'
					acc.match = dim
				} else {
					if (isInSameRow && (acc.rowIndex === null || acc.rowIndex === undefined)) {
						const [,rowIndex] = getCell(rows,c.id)
						acc.rowIndex = rowIndex
					}

					if (dim.x <= acc.topLeft.x && dim.y <= acc.topLeft.y) {
						acc.topLeft.x = dim.x
						acc.topLeft.y = dim.y
					} 
					if (dim.right >= acc.bottomRight.x && dim.bottom >= acc.bottomRight.y) {
						acc.bottomRight.x = dim.right
						acc.bottomRight.y = dim.bottom
					}
				}
			}
			return acc 
		}, { match:null, topLeft:{ x:10000000,y:10000000 }, bottomRight:{ x:0,y:0 }, rowIndex:null })

		if (results.match)
			return { cell:results.match }
		else {
			const side = x <= results.topLeft.x ? 'left' : x >= results.bottomRight.x ? 'right' : 'in'
			const pos = y <= results.topLeft.y ? 'above' : y >= results.bottomRight.y ? 'under' : 'in' 
			if (pos == 'above')
				results.rowIndex = 0
			else if (pos == 'under')
				results.rowIndex = rows.length - 1

			return { cell:null, virtualPos: { rowIndex:results.rowIndex, pos, side } }	
		}
	}

	const setCellSelectedStates = selectedCellIds => {
		const multi = selectedCellIds.length > 1
		if (noresize != multi)
			noresize = multi

		for (let state of cellSelectedStates) {
			const selected = selectedCellIds.includes(state.id)
			const stateValue = state.getValue()
			if (selected != stateValue.selected || (selected && multi != stateValue.multi))
				state.setValue({ selected, multi })
		}	
	}

	/**
	 * Gets the cell's layers that are currently toggled. That toggled state is 
	 * stored in the global variable 'rowLayers'.
	 * NOTE: There can be more than one layer toggled at the same time, which means they are all visible. 
	 * 
	 * @param  {cell} 		cell
	 * 
	 * @return {[Layer]}	toggledLayers
	 */
	const getToggledLayers = (cell) => {
		if (!cell || !cell.layers || !rowLayers)
			return null 

		const [,rowIndex,cellIndex] = getCell(rows, cell.id)
		const layerIds = (rowLayers[rowIndex]||'').split(',')[cellIndex].replace(/.*:/,'').split(';')
		
		return cell.layers.filter(l => layerIds.some(layerId => l.id == layerId))
	}

	/**
	 * Parses the data string from the dragged item. 
	 * 
	 * @param  {String} data				e.g., 'comp:textfield,type:native'
	 * @param  {String} cellId				e.g., '_1234'
	 * 
	 * @return {String}	component.id		e.g., 'textfield'
	 * @return {String}	component.type		e.g., 'native'
	 * @return {String}	component.config	e.g., {}
	 */
	const getComponentDetails = data => {
		let [,id,,type,,componentData=''] = data.match(/comp:(.*?)(,\s*type:(.*?)){0,1}(,\s*data:(.*?)){0,1}$/) || []
		if (id) {
			id = id.trim()
			type = type||'native'
			let jsonData
			try {	
				jsonData = componentData ? JSON.parse(decodeBase64(componentData)) : {}
			} catch(err) {
				jsonData = (() => ({}))(err)
			}

			if (!jsonData.id)
				jsonData.id = id
			if (!jsonData.type)
				jsonData.type = type

			return { id, type, config:jsonData }
		} else 
			return null
	}

	/**
	 * [description]
	 * @param  {String}  strData						e.g., 'comp:textfield,type:native'
	 * @return {String}  cellDetails.id							
	 * @return {Boolean} cellDetails.new				If true, this cell must be created in a new or existing row.	
	 * @return {Number}  cellDetails.rowIndex			Position where the cell should be placed (1).					
	 * @return {Number}  cellDetails.belowRowIndex		Position where the cell should be placed (1).
	 * @return {Number}  cellDetails.aboveRowIndex		Position where the cell should be placed (1).
	 * @return {Boolean} cellDetails.singleCellRow		If true, only 'belowRowIndex' or 'aboveRowIndex' are valid (not 'rowIndex').
	 * @return {String}  cellDetails.neighbourId				
	 * @return {String}  cellDetails.neighbourSide	
	 * 
	 * @return {Void}
	 */
	const addComponent = (strData, cellDetails) => {
		if (!cellDetails)
			return 

		const comp = getComponentDetails(strData)
		if (!comp)
			return 
		
		if (comp.id && comp.id == id)
			throw new Error(`Infinite loop due to Lineup component self-reference (compId: ${id}${comp.config && comp.config.name ? `, name: ${comp.config.name}` : ''}).`)	

		const layers = [{ id:getId() , name:'New Layer', toggled:true, locked:false, component: comp.config }]

		let cell = {}
		// Dropping the component into a new cell
		if (cellDetails.new) {
			cell.id = getId()
			cell.defaultLayerId = 0
			if (cellDetails.singleCellRow)
				udpateGrids([{
					id: cell.id,
					newCell: {
						singleCellRow: cellDetails.singleCellRow,
						aboveRowIndex:cellDetails.aboveRowIndex,
						belowRowIndex:cellDetails.belowRowIndex,
						layers
					}
				}], null, { caller:'addComponent:new_single_row_cell' })
			else
				udpateGrids([{
					id: cell.id,
					newCell: {
						rowIndex:cellDetails.rowIndex,
						neighbourId:cellDetails.neighbourId,
						neighbourSide:cellDetails.neighbourSide,
						layers
					}
				}], null, { caller:'addComponent:new_cell' })
		} else if (cellDetails.id) // Dropping the component into an existing cell
			cell = (getCell(rows,cellDetails.id)||[])[0]
		
		emitLayerAdd({ cell, new:cellDetails.new, layers })
		tick().then(() => setSelectedCellIds([cell.id]))		
	}

	/**
	 * Checks whether a cell's new position is different from its current position. 
	 * 
	 * @param  {Number}  from.rowIndex
	 * @param  {Number}  from.cellIndex
	 * @param  {Boolean} from.singleCellRow
	 * @param  {Boolean} to.singleCellRow
	 * @param  {Number}  to.rowIndex
	 * @param  {Number}  to.aboveRowIndex
	 * @param  {Number}  to.belowRowIndex
	 * @param  {String}  to.neighbourId
	 * @param  {String}  to.neighbourSide
	 * 
	 * @return {Boolean}
	 */
	const differentDestination = (from,to) => {
		if (!from || !to)
			return false

		const aboveRowIndexExists = exists(to.aboveRowIndex)
		const belowRowIndexExists = exists(to.belowRowIndex)
		let toRowIndex = aboveRowIndexExists ? to.aboveRowIndex : belowRowIndexExists ? to.belowRowIndex : to.rowIndex
		if (!(from.singleCellRow && from.rowIndex == toRowIndex)) 
			toRowIndex = aboveRowIndexExists ? to.aboveRowIndex-1 : belowRowIndexExists ? to.belowRowIndex+1 : to.rowIndex

		if (toRowIndex != from.rowIndex)
			return true

		if (to.singleCellRow)
			return from.cellIndex !== 0

		const [,,neighbourIndex] = getCell(rows, to.neighbourId)
		const lastIndex = rows[toRowIndex].cells.length-1
		let toCellIndex = to.neighbourSide == 'left' ? (neighbourIndex-1) : (neighbourIndex+1)
		if (toCellIndex < 0)
			toCellIndex = 0
		else if (toCellIndex > lastIndex)
			toCellIndex = lastIndex

		return from.cellIndex != toCellIndex
	}	

	const moveDraggedCell = (cellId, to) => {
		if (!cellId || !to)
			return 

		const [cell, rowIndex, cellIndex] = getCell(rows, cellId)
		const rowCells = rows[rowIndex].cells
		if (!rowCells)
			return

		const from = {
			rowIndex,
			cellIndex,
			singleCellRow:rowCells.length == 1,
			rowCells
		}
		
		if (differentDestination(from,to))
			moveCellToRow(cell, from, to)
	}

	const getComponentPreviewStyle = (top, left, width, height) => `top:${top}px;left:${left}px;width:${width}px;height:${height}px;`

	/**
	 * Gets the position of component based on the current cursor's cartesian coordinates. 
	 * 
	 * @param  {Number}  pos.x
	 * @param  {Number}  pos.y
	 * @param  {String}  movingCellId			Optional. When it exists, this means that the cell is moving, 
	 *                                 			in contrast with a component being dropped.
	 * 
	 * @return {Number}  output.preview.top
	 * @return {Number}  output.preview.left
	 * @return {Number}  output.preview.width
	 * @return {Number}  output.preview.height
	 * @return {String}  output.cell.id							
	 * @return {Boolean} output.cell.new				If true, this cell must be created in a new or existing row.	
	 * @return {Number}  output.cell.rowIndex			Position where the cell should be placed (1).					
	 * @return {Number}  output.cell.belowRowIndex		Position where the cell should be placed (1).
	 * @return {Number}  output.cell.aboveRowIndex		Position where the cell should be placed (1).
	 * @return {Boolean} output.cell.singleCellRow		If true, only 'belowRowIndex' or 'aboveRowIndex' are valid (not 'rowIndex').
	 * @return {String}  output.cell.neighbourId				
	 * @return {String}  output.cell.neighbourSide				
	 *
	 * (1) 'belowRowIndex' takes precedence over 'aboveRowIndex' which takes precedence over 'rowIndex'.
	 */
	const getComponentGridPosition = (pos, movingCellId) => {
		const x = pos.clientX
		const y = pos.clientY

		const result = findCellHoveredOn(x, y)

		if (!result.cell)
			return null
		if (!getDim)
			getDim = nodeDim(node)

		const cellHovered = result.cell
		const componentContainedInCell = !cellHovered.verticalEdge && !cellHovered.horizontalEdge
		const gridDim = getDim(true)
		const gridStart = paddingObj.left||0
		const gridWidth = gridDim.width - gridStart - (paddingObj.right||0)

		if (componentContainedInCell) 
			return movingCellId ? null : { 
				preview: {
					top: cellHovered.y-gridDim.y,
					left: cellHovered.x-gridDim.x,
					width: cellHovered.width,
					height: cellHovered.height
				},
				cell: {
					id: cellHovered.id,
					new: false
				}
			}
		else {
			const [,rowIndex, cellIndex] = getCell(rows, cellHovered.id)
			const maxRowIndex = rows.length-1
			if (cellHovered.horizontalEdge) { // A. SINGLE CELL ROW PREVIEW
				const placeOnTop = cellHovered.verticalZone == 'top'
				if (rowIndex == 0 && placeOnTop) // A.1. TOP PREVIEW
					return {
						preview: {
							top: cellHovered.y-gridDim.y, 
							left: gridStart, 
							width: gridWidth, 
							height: getEdgeThickness(cellHovered.height) 
						},
						cell: {
							new:true,
							singleCellRow: true,
							aboveRowIndex: 0
						}
					}
				else if (rowIndex == maxRowIndex && !placeOnTop) { // A.2. BOTTOM PREVIEW
					const thickness = getEdgeThickness(cellHovered.height)
					const offset = cellHovered.height - thickness
					return {
						preview: {
							top: cellHovered.y-gridDim.y+offset, 
							left: gridStart, 
							width: gridWidth, 
							height: thickness 
						},
						cell: {
							new:true,
							singleCellRow: true,
							belowRowIndex: maxRowIndex
						}
					}
				} else if (placeOnTop) { // A.3. IN BETWEEN ROWS PREVIEW (ABOVE THE CURRENT CELL)
					const aboveCellId = rows[rowIndex-1].cells[0].id
					const aboveRowHeight = cellDimensions.find(c => c.id == aboveCellId).getDim(true).height
					const thickness = getEdgeThickness(cellHovered.height,aboveRowHeight)
					return {
						preview: {
							top: cellHovered.y-gridDim.y-thickness, 
							left: gridStart, 
							width: gridWidth, 
							height: thickness*2 
						},
						cell: {
							new:true,
							singleCellRow: true,
							aboveRowIndex: rowIndex
						}
					}
				} else { // A.4. IN BETWEEN ROWS PREVIEW (BELOW THE CURRENT CELL)
					const belowCellId = rows[rowIndex+1].cells[0].id
					const belowRowHeight = cellDimensions.find(c => c.id == belowCellId).getDim(true).height
					const thickness = getEdgeThickness(cellHovered.height,belowRowHeight)
					return {
						preview: {
							top: cellHovered.y-gridDim.y+cellHovered.height-thickness, 
							left: gridStart, 
							width: gridWidth, 
							height: thickness*2 
						},
						cell: {
							new:true,
							singleCellRow: true,
							belowRowIndex: rowIndex
						}
					}
				}
			} else { // B. EXISTING ROW PREVIEW
				const placeLeft = cellHovered.horizontalZone == 'left'
				const rowCells = rows[rowIndex].cells
				const maxCellIndex = rowCells.length-1
				const cell = {
					new:true,
					rowIndex,
					neighbourId: rowCells[cellIndex].id,
					neighbourSide: cellHovered.horizontalZone
				}

				// Fix the case where the cell's neighbour is itself
				if (movingCellId && rowCells.length > 1 && cell.neighbourId == movingCellId) {
					const [otherNeightbour, otherSide] = cell.neighbourSide == 'left' 
						? [rowCells[cellIndex-1], 'right'] 
						: [rowCells[cellIndex+1], 'left']
					
					if (otherNeightbour) {
						cell.neighbourId = otherNeightbour.id,
						cell.neighbourSide = otherSide
					}
				}

				if (cellIndex == 0 && placeLeft) // B.1. FIRST CELL PREVIEW
					return {
						preview: {
							top: cellHovered.y-gridDim.y, 
							left: cellHovered.x-gridDim.x, 
							width: getEdgeThickness(cellHovered.width), 
							height: cellHovered.height 
						},
						cell
					}
				else if (cellIndex == maxCellIndex && !placeLeft) { // B.2. LAST CELL PREVIEW
					const thickness = getEdgeThickness(cellHovered.width)
					const offset = cellHovered.width - thickness
					return {
						preview: {
							top: cellHovered.y-gridDim.y, 
							left: cellHovered.x-gridDim.x+offset, 
							width: thickness, 
							height: cellHovered.height 
						},
						cell
					}
				} else if (placeLeft) { // B.3. LEFT OF CURRENT CELL PREVIEW
					const leftCellId = rowCells[cellIndex-1].id
					const leftCellWidth = cellDimensions.find(c => c.id == leftCellId).getDim(true).width
					const thickness = getEdgeThickness(leftCellWidth, cellHovered.width)
					return {
						preview: {
							top: cellHovered.y-gridDim.y, 
							left: cellHovered.x-gridDim.x-thickness, 
							width: thickness*2, 
							height: cellHovered.height 
						},
						cell
					}
				} else { // B.4. RIGHT OF CURRENT CELL PREVIEW
					const rightCellId = rowCells[cellIndex+1].id
					const rightCellWidth = cellDimensions.find(c => c.id == rightCellId).getDim(true).width
					const thickness = getEdgeThickness(rightCellWidth, cellHovered.width)
					return {
						preview: {
							top: cellHovered.y-gridDim.y, 
							left: cellHovered.x-gridDim.x+cellHovered.width-thickness, 
							width: thickness*2, 
							height: cellHovered.height 
						},
						cell
					}
				}
			}
		}
	}

	/**
	 * Displays a colored area that shows where the cell will be created or moved. This is done by setting the 
	 * 'componentPreviewCssStyle' variable. 
	 * 
	 * @param  {Number} cursorPos.clientX	
	 * @param  {Number} cursorPos.clientY		
	 * @param  {String} movingCellId			Optional. When it exists, this means that the cell is moving, 
	 *                                 			in contrast with a component being dropped.
	 * 
	 * @return {Void}
	 */
	const previewCellPosition = (cursorPos, movingCellId) => {
		const gridPos = getComponentGridPosition(cursorPos, movingCellId)
		if (!gridPos || !gridPos.preview) {
			cellDropPos = null
			hideComponentPreview()
		}
		else {
			cellDropPos = gridPos.cell
			componentPreviewCssStyle = getComponentPreviewStyle(
				gridPos.preview.top,
				gridPos.preview.left,
				gridPos.preview.width,
				gridPos.preview.height)
		}
	}

	/**
	 * Used when the edit mode it toggled. 
	 * 
	 * @return {Object} dummy object
	 */
	const resetToggledCellLayers = () => {
		// console.log('RESET')
		toggledCellLayers = {}
		return toggledCellLayers
	}

	const hideComponentPreview = () => componentPreviewCssStyle = ''

	const addFallback = (v,fallback) => exists(v) ? v : fallback

	/**
	 * 
	 * 
	 * @param  {Number}	onHover.elevation
	 * @param  {Number}	onHover.translate
	 * @param  {Number}	onHover.translateX
	 * @param  {Number}	onHover.translateY
	 * @param  {Number}	onHover.scale
	 * @param  {Number}	onHover.scaleX
	 * @param  {Number}	onHover.scaleY	
	 * @param  {Number}	onHover.transitionDuration	Default 100.
	 * @param  {String}	onHover.transitionCurve		Default 'ease-in-out'.
	 * 
	 * @return {String} config.boxShadow
	 * @return {String} config.transition
	 * @return {String} config.transform
	 */
	const getHoverConfig = (onHover, elevationStrength, elevationColor) => {
		const config = { 
			boxShadow: 'none',
			transform: 'none',
			transition:'none', 
		}
		if (!onHover)
			return config

		const dur = onHover.transitionDuration || 100
		const curve = onHover.transitionCurve || 'ease-in-out'
		const cssTransition = `${dur}ms ${curve}`

		const _hoverElevation = exists(onHover.elevation) ? onHover.elevation : null
		const _hoverTranslate = exists(onHover.translate) || exists(onHover.translateX) || exists(onHover.translateY) 
			? `translate(${addUnit(onHover.translate||onHover.translateX||0)}, ${addUnit(onHover.translate||onHover.translateY||0)})` 
			: null
		const _hoverScale = exists(onHover.scale) || exists(onHover.scaleX) || exists(onHover.scaleY) 
			? `scale(${addFallback(onHover.scale||onHover.scaleX,1)}, ${addFallback(onHover.scale||onHover.scaleY,1)})` 
			: null
		
		let propsExists = false
		const cssTransitionProps = {}
		if (_hoverElevation !== null) {
			propsExists = true 
			config.boxShadow = getBoxShadow({elevation:_hoverElevation, strength:elevationStrength, color:elevationColor})
			cssTransitionProps['box-shadow'] = cssTransition
		}

		if (_hoverTranslate !== null || _hoverScale !== null) {
			propsExists = true 
			config.transform = [_hoverTranslate, _hoverScale].filter(x => x).join(' ')
			cssTransitionProps['transform'] = cssTransition
		}

		if (propsExists)
			config.transition = transition.css(cssTransitionProps, true, false, true)

		return config
	}

	const getRootVars = ({ padding, margin, backgroundColor, border, borderRadius, borderColor, elevation, elevationStrength, elevationColor, theme, width, height, cursor, onHover }) => {

		const _hoverConfig = getHoverConfig(onHover, elevationStrength, elevationColor)
		if (removeCssVariablesFromBody)
			removeCssVariablesFromBody()

		let _theme = ''
		if (theme) {
			if (theme.body)
				removeCssVariablesFromBody = setVariables(theme)
			else 
				_theme = setVariables(theme)
		}

		return {
			classes: [
				onHover ? 'lu-component-hover' : null
			].filter(x => x).join(' '),
			rootVars: css(`
			${_theme}
			${sizeCssVariables('--lu-width', width, { all:true, stringOutput:true })}
			${sizeCssVariables('--lu-height', height, { all:true, stringOutput:true })}
			--lu-padding: ${paddingParse(padding).css||0};
			--lu-margin: ${paddingParse(margin).css||0};
			--lu-background-color: ${backgroundColor ? chooseColor(backgroundColor) : 'transparent'};
			--lu-border: ${border||'0px'};
			--lu-border-radius: ${borderRadius ? cornerParse(borderRadius).css : '0px'};
			--lu-border-color: ${borderColor||'transparent'};
			--lu-cursor: ${cursor||'default'};
			--lu-box-shadow: ${elevation ? getBoxShadow({ elevation, strength:elevationStrength, color:elevationColor }) : 'none'};
			--lu-hover-box-shadow: ${_hoverConfig.boxShadow};
			--lu-hover-transform: ${_hoverConfig.transform};
			--lu-hover-trans: ${_hoverConfig.transition};
			`)
		}
	}

	/* 
	=======================================================
	END - PRIVATE FUNCTIONS
	=======================================================
	*/









	/* 
	=======================================================
	START - EVENT HANDLERS
	=======================================================
	*/

	/**
	 * Mutates the cells array using the sequence of new width. 
	 * 
	 * @param  {Number}	 data.id					Row's ID 
	 * @param  {Number}	 data.height				Row height in px
	 * @param  {Object}  data.cells[].id			Cell's width (e.g., '33%')
	 * @param  {Number}  data.cells[].width			Cell's width in pixels (e.g., 120). Only valid when 'fixedWidth' is true.
	 * @param  {String}  data.cells[].widthUnit	
	 *
	 * @return {Void}
	 */
	const onRowResize = data => {
		const cellMutations = !data.cells ? null : data.cells.map(c => ({
			id:c.id,
			updateSize: cell => {
				if (cell.width != c.width || cell.widthUnit != c.widthUnit) {
					// if (c.width < 0)
					// 	console.log(`Cell ${cell.id} (${c.width}${c.widthUnit}) - onRowResize`)

					return { ...cell, width:c.width, widthUnit:c.widthUnit }
				} else 
					return cell
			}
		}))
		const rowMutations = data.height === null || data.height === undefined ? null : [{
			id:data.id,
			updateSize: row => row && row.height != data.height ? { ...row, height:data.height } : row
		}]
		udpateGrids(cellMutations, rowMutations, { caller:'onRowResize', norender:true })
	}

	/**
	 * 
	 * @param  {String} data.id			Cell's ID.
	 * @param  {Number}	data.clientX	Cursor X position.
	 * @param  {Number}	data.clientY	Cursor Y position.
	 * 
	 * @return {Void}
	 */
	const onCellDragStart = (data) => {
		cellHoverEffectEnabled = false
		draggedCellId = data.id
		lastXpos = data.clientX || null
		lastYpos = data.clientY || null
	}

	const onMouseMove = e => {
		onDragEnd()

		if (store.mouseMoveHandler)
			store.mouseMoveHandler(e.clientX, e.clientY)
	}

	const onMouseUp = e => {
		if (resizingVal)
			resizing.update(() => false)

		if (store.mouseMoveHandler) {
			store.mouseMoveHandler(e.clientX, e.clientY, true)
			store.mouseMoveHandler = null
		}
	}

	/**
	 * Fired when a cell is clicked. 
	 * 
	 * @param  {Cell}		data.cell
	 * @param  {[Layer]}	data.layers
	 * 
	 * @return {Void}
	 */
	const onCellMouseDown = data => {
		selectCell(data.cell, data.layers)
	}

	/**
	 * 
	 * @param  {String} direction	Valid values: 'up', 'right', 'down', 'left' 
	 * @param  {Number} cellIndex
	 * @param  {Number} rowIndex
	 * 
	 * @return {Void}
	 */
	const onNextCell = ({ direction, cellIndex }, rowIndex) => {
		const nextCellId = getNextCellId({ direction, cellIndex, rowIndex })
		if (nextCellId) {
			const [cell] = getCell(rows, nextCellId)
			selectCell(cell, getToggledLayers(cell))
		}
	}

	const onKeyDown = e => {
		if (e.key == 'Meta' || e.key == 'Control') {
			e.preventDefault()
			commandControlKeyDown = true
		}
		if (commandControlKeyDown && e.key == 'c') {
			if (selectedCellIds && selectedCellIds[0])
				copiedCellIds = [...selectedCellIds]
			else
				copiedCellIds = null
		}
		if (copiedCellIds && e.key == 'v')
			paste(copiedCellIds)
	}

	const onKeyUp = e => {
		if (e.key == 'Meta' || e.key == 'Control') {
			e.preventDefault()
			commandControlKeyDown = false
		}
	}

	/**
	 * 
	 * @param  {Cell}	 data.cell
	 * @param  {Object}	 data.value
	 * @param  {String}	 data.layer.id
	 * @param  {String}	 data.layer.component
	 * @param  {Object}	 data.layer.triggers
	 * @param  {String}	 data.name				Field name
	 * @param  {String}	 data.type				Field type, e.g., 'textfield', 'slider'
	 * 
	 * @return {Void}	
	 */
	const onComponentValueChange = (data) => {
		if (!data.value && data.mountEvent)
			return
		
		if (!edit && data && data.type)
			mutateCellValues(cellValues, data)

		emitComponentValueChange(data)
		executeTriggers(data.cell, data.layer, data.value, 'change')
	}

	/**
	 * 
	 * @param  {Cell}    data.cell					
	 * @param  {Object}	 data.layer
	 * 	
	 * @return {Void}
	 */
	const onComponentClick = (data) => {
		const value = assembleAllValuesAndEmitClick(data)
		executeTriggers(data.cell, data.layer, value, 'click')
	}

	// The tricky part of this function is to anticipate what the current dragged cell looks like after it has been 
	// moved before it is moved. Indeed, we need to proceed to that re-rendering before the cursor actually hover 
	// another cell, otherwise, the re-rendering might rerender the dragged cell in a position where the cursor is 
	// already in another cell, which creates anothe re-render which creates a buggy experience for the person 
	// dragging the cell.
	const onDragOver = e => {
		e.preventDefault()
		
		const componentCell = e.dataTransfer.types && e.dataTransfer.types.indexOf('component') >= 0
		previewCellPosition(e, componentCell ? null : draggedCellId)
	}

	const onDrop = (e) => {
		const data = e.dataTransfer.getData('text')

		if (/^comp:/.test(data) && cellDropPos)  // detect dropping component in cell
			addComponent(data, cellDropPos)
		else if (/^_/.test(data)) {
			moveDraggedCell(draggedCellId, cellDropPos)
			tick().then(() => setSelectedCellIds([data]))
		}
		
		onDragEnd()
	}

	const onDragEnd = () => {
		if (!cellHoverEffectEnabled)
			cellHoverEffectEnabled = true
		if (componentPreviewCssStyle)
			hideComponentPreview()
		if (cellDropPos)
			cellDropPos = null

		if (draggingVal)
			dragging.update(() => false)
	}

	const onViewportChange = data => {
		// resetToggledCellLayers()
		emitViewportChange(data)
	}

	onDestroy(() => {
		store.destroy()
		if (removeCssVariablesFromBody)
			removeCssVariablesFromBody()
	})

	/* 
	=======================================================
	END - EVENT HANDLERS
	=======================================================
	*/









	/* 
	=======================================================
	START - EVENT LIFECYCLE
	=======================================================
	*/

	$: inject = injectData(data)
	$: draggingVal = $dragging
	$: resizingVal = $resizing
	$: paddingObj = paddingParse(padding)
	$: vars = getRootVars({ 
		padding: inject(padding), 
		margin: inject(margin), 
		backgroundColor: inject(backgroundColor), 
		border: inject(border), 
		borderRadius: inject(borderRadius), 
		borderColor: inject(borderColor), 
		elevation: inject(elevation), 
		elevationStrength: inject(elevationStrength), 
		elevationColor: inject(elevationColor), 
		theme: inject(theme), 
		width: inject(width), 
		height: inject(height), 
		cursor: inject(cursor), 
		onHover:edit ? null : onHover })
	$: cellHoverEnabledClass = edit && cellHoverEffectEnabled ? 'cell-hover-enabled' : ''
	$: stringViewportRanges = getViewportRanges(grids, { format:'string' }) // e.g., '0,500,800,1200'
	$: viewportRanges = stringViewportRanges.split(',').map(x => x*1) // e.g., [0,500,800,1200]
	$: componentStore = addConfig([...(componentDefs||[]), ...COMPONENTS])
	$: vp = getViewportWidth(viewportWidth)
	$: currentViewportWidth = getCurrentBreakpointViewport(viewportRanges, vp, onViewportChange) // e.g., 500
	$: rows = renderBreakpointRows(grids, currentViewportWidth) 
	$: rowLayers = getToggledRowLayersAndSetToggledCellLayers(rows, resetToggledCellLayers(edit)) 
	$: rowRefHeight = rows.length == 1 && height ? height : null // (1)
	$: { if (deleteCounter > 0) deleteCells({ selected:true }) }
	$: { setCellSelectedStates(selectedCellIds) }

	// (1) 	This is used to override the row height in the situation where the height has been explicitely specified on a
	// 		single row lineup component.

	pageSetup(page)
	
	setContext('componentstore', {
		/**
		 * Get component details. 
		 * 
		 * @param  {String} 		id		e.g., 'textfield', 'slider'
		 * 
		 * @return {String}			id		e.g., 'textfield', 'slider'
		 * @return {SvelteClass}	class	e.g., Textfield, Slider
		 * @return {Object}			config	
		 */
		getComponentById: id => componentStore.find(c => c.id == id)
	})

	/* 
	=======================================================
	END - EVENT LIFECYCLE
	=======================================================
	*/
</script>

<svelte:window 
	bind:innerWidth={viewportWidth} 
	on:mousemove={edit ? onMouseMove : null} 
	on:mouseup={edit ? onMouseUp : null}
	/>
<div 
	id={ID} 
	data-type="lineup"  
	data-id={id}
	data-name={name||''}
	class="{vars.rootVars} {rootClass} {cellHoverEnabledClass} {vars.classes}"
	bind:this={node}
	on:keydown={onKeyDown}
	on:keyup={onKeyUp}
	on:dragover={edit ? onDragOver : null}
	on:drop={edit ? onDrop : null}
	>
	{#each rowLayers as cellLayersConfig, rowIndex (rows[rowIndex].id)}
	<Row 
		row={rows[rowIndex]}
		refHeight={rowRefHeight}
		index={rowIndex}
		storeId={store.id}
		{edit}
		{cellLayersConfig}
		{inject}
		on:valuechange={d => onComponentValueChange(d.detail)}
		on:componentclick={d => onComponentClick(d.detail)}
		on:cellmousedown={d => onCellMouseDown(d.detail)}
		on:deletecell={d => deleteCells(d.detail)}
		on:resize={d => onRowResize(d.detail)}
		on:celldragstart={d => onCellDragStart(d.detail)}
		on:celldragend={onDragEnd}
		on:nextcell={d => onNextCell(d.detail, rowIndex)}
		on:cellmount={d => registerCell(d.detail)}
		on:celldestroy={d => deregisterCell(d.detail.id)}>
	</Row>
	{/each}
	{#if edit}
	<div class="lu-component-preview" style="{componentPreviewCssStyle}"></div>
	{/if}
</div>
